/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/activities_notes_junction-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-activities_notes_junction-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/activities_notes_junction-entity-config.ts
 * - Run: npm run hooks:gen (regenerates this file)
 * - Or run: npm run db:full-workflow (does everything)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, activity_id, note_id
 * - Sortable fields: created_at, updated_at
 * - Required fields: activity_id, note_id
 * - Readonly fields: id, trig_note_title, trig_note_type, trig_note_is_internal, trig_note_created_by, trig_note_content, trig_activity_name, trig_activity_status_name, trig_activity_type_name, trig_activity_scheduled_date, created_at, updated_at, trig_note_assigned_to, trig_activity_assigned_users
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/activities_notes_junction-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-activities_notes_junction-api-client.ts (API client)
 * - client/api/generated_schemas/activities_notes_junction.ts (TypeBox schemas and types)
 *
 * Generated at: 2026-08-20T18:11:43.565Z
 * Source config: config/entities/activities_notes_junction-entity-config.ts
 * Public fields: 17/17 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ActivitiesNotesJunctionClient, ActivitiesNotesJunctionQueryOptions } from '../generated_clients/generated-activities_notes_junction-api-client.js';
import {
  ActivitiesNotesJunction,
  ActivitiesNotesJunctionInsert,
  ActivitiesNotesJunctionUpdate,
  ActivitiesNotesJunctionFieldConfig
} from '../generated_schemas/generated-activities_notes_junction-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/activities_notes_junction_business_hooks/

// Query keys with enhanced caching strategy
export const activitiesNotesJunctionKeys = {
  all: ['activities_notes_junction'] as const,
  lists: () => [...activitiesNotesJunctionKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...activitiesNotesJunctionKeys.lists(), { filters }] as const,
  details: () => [...activitiesNotesJunctionKeys.all, 'detail'] as const,
  detail: (id: string) => [...activitiesNotesJunctionKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...activitiesNotesJunctionKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...activitiesNotesJunctionKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...activitiesNotesJunctionKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...activitiesNotesJunctionKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useActivitiesNotesJunctionClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new ActivitiesNotesJunctionClient();
};

/**
 * Hook to get all activities_notes_junction with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with activities_notes_junction data
 */
export const useActivitiesNotesJunctionList = (options?: ActivitiesNotesJunctionQueryOptions) => {
  const client = useActivitiesNotesJunctionClient();

  return useQuery({
    queryKey: activitiesNotesJunctionKeys.list(options?.filters || {}),
    queryFn: () => client.getAll(options),
    // Always reconcile with the server on mount, even when persisted/cached
    // data is restored (field PWA offline cache) or a cross-device write
    // happened elsewhere. Cached data still paints instantly; the refetch
    // corrects it. Offline, the refetch simply fails and the cache is kept.
    // Nobody should ever see stale operational data. (card rKkWHT_XhbXU)
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

/**
 * Hook to get a single activities_notes_junctio by ID
 *
 * @param id - The activities_notes_junctio ID
 * @returns React Query result with single activities_notes_junctio data
 */
export const useActivitiesNotesJunction = (id: string) => {
  const client = useActivitiesNotesJunctionClient();

  return useQuery({
    queryKey: activitiesNotesJunctionKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
    // Always reconcile with the server on mount so a reload / navigation never
    // paints a stale restored (persisted) detail snapshot. (card rKkWHT_XhbXU)
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/activities_notes_junction
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/activities_notes_junction_business_hooks/
 */

/**
 * Hook to create a new activities_notes_junctio
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: activity_id, note_id
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, trig_note_title, trig_note_type, trig_note_is_internal, trig_note_created_by, trig_note_content, trig_activity_name, trig_activity_status_name, trig_activity_type_name, trig_activity_scheduled_date, created_at, updated_at, trig_note_assigned_to, trig_activity_assigned_users
 *
 * @returns React Query mutation for creating activities_notes_junctio
 */
export const useCreateActivitiesNotesJunction = () => {
  const client = useActivitiesNotesJunctionClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: ActivitiesNotesJunctionInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activitiesNotesJunctionKeys.all });
    },
  });
};

/**
 * Hook to update an existing activities_notes_junctio
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, trig_note_title, trig_note_type, trig_note_is_internal, trig_note_created_by, trig_note_content, trig_activity_name, trig_activity_status_name, trig_activity_type_name, trig_activity_scheduled_date, created_at, updated_at, trig_note_assigned_to, trig_activity_assigned_users
 *
 * @returns React Query mutation for updating activities_notes_junctio
 */
export const useUpdateActivitiesNotesJunction = () => {
  const client = useActivitiesNotesJunctionClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ActivitiesNotesJunctionUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: activitiesNotesJunctionKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: activitiesNotesJunctionKeys.lists() });
    },
  });
};

/**
 * Hook to delete a activities_notes_junctio
 *
 * @returns React Query mutation for deleting activities_notes_junctio
 */
export const useDeleteActivitiesNotesJunction = () => {
  const client = useActivitiesNotesJunctionClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activitiesNotesJunctionKeys.all });
    },
  });
};

/**
 * Hook to count activities_notes_junction with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, activity_id, note_id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useActivitiesNotesJunctionCount = (filter?: Partial<Record<typeof ActivitiesNotesJunctionFieldConfig.filterable[number], any>>) => {
  const client = useActivitiesNotesJunctionClient();
  
  return useQuery({
    queryKey: activitiesNotesJunctionKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useActivitiesNotesJunctionFieldConfig = () => {
  return ActivitiesNotesJunctionFieldConfig;
};